/**
 * React Query hooks for CRM CSV import/export.
 *
 * Import preview and execute are mutations (one-shot, no cached result
 * for the same payload). The preview/result still live in local state
 * because callers expect to consume them step-by-step in the wizard UI.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { CrmImportExecuteResult, CrmImportPreviewResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useImportExport() {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<CrmImportPreviewResult | null>(null);
  const [importResult, setImportResult] = useState<CrmImportExecuteResult | null>(null);

  const exportMutation = useMutation({
    mutationFn: ({
      entityType,
      filters,
    }: {
      entityType: string;
      filters?: { search?: string; status?: string };
    }) => api.exportCrmEntities(entityType, filters),
    onSuccess: (csv, { entityType }) => {
      // Trigger browser download.
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-${entityType}-export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Export complete', description: `${entityType} exported successfully` });
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to export data', variant: 'destructive' }),
  });

  const previewMutation = useMutation({
    mutationFn: ({ entityType, csvData }: { entityType: string; csvData: string }) =>
      api.previewCrmImport(entityType, csvData),
    onSuccess: (result) => {
      setPreview(result);
      setImportResult(null);
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to parse CSV', variant: 'destructive' }),
  });

  const executeMutation = useMutation({
    mutationFn: ({
      entityType,
      rows,
      fieldMapping,
    }: {
      entityType: string;
      rows: Record<string, string>[];
      fieldMapping: Record<string, string>;
    }) => api.executeCrmImport(entityType, rows, fieldMapping),
    onSuccess: (result, { entityType }) => {
      setImportResult(result);
      toast({
        title: 'Import complete',
        description: `${result.successCount} of ${result.totalRows} rows imported successfully`,
      });
      // Invalidate the relevant list cache so the UI picks up new rows.
      if (entityType === 'contacts') {
        queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts.all() });
      } else if (entityType === 'companies') {
        queryClient.invalidateQueries({ queryKey: queryKeys.crm.companies.all() });
      } else if (entityType === 'deals') {
        queryClient.invalidateQueries({ queryKey: queryKeys.crm.deals.all() });
      }
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to execute import', variant: 'destructive' }),
  });

  const reset = useCallback(() => {
    setPreview(null);
    setImportResult(null);
  }, []);

  return {
    preview,
    importResult,
    loading: previewMutation.isPending || executeMutation.isPending,
    exporting: exportMutation.isPending,
    exportEntities: async (entityType: string, filters?: { search?: string; status?: string }) => {
      try {
        await exportMutation.mutateAsync({ entityType, filters });
      } catch {
        // toast already shown
      }
    },
    previewImport: async (entityType: string, csvData: string) => {
      try {
        return await previewMutation.mutateAsync({ entityType, csvData });
      } catch {
        return null;
      }
    },
    executeImport: async (
      entityType: string,
      rows: Record<string, string>[],
      fieldMapping: Record<string, string>,
    ) => {
      try {
        return await executeMutation.mutateAsync({ entityType, rows, fieldMapping });
      } catch {
        return null;
      }
    },
    reset,
  };
}
